import styled from 'styled-components';
import OrderOption from './OrderOption';
import OrderFiles from './order-files/OrderFiles';
import OrderAttachedFiles from './OrderAttachedFiles';
import OrderLog from './OrderLog';
import { useCallback, useEffect } from 'react';
import { useLoading } from '@/context/LoadingContext';
import { getOrderDetail } from '@/api/order-detail/getOrderDetail';
import { useDispatch } from 'react-redux';
import { order } from '@/stores/slices/order/orderSlice';
import { useAppSelector } from '@/stores/hooks';
import { ORDER_STATUS_TYPE } from '@/api/type/commonTypes';

interface OrderDetailProps {
  orderId: string;
  patientName: string;
  orderStatus: ORDER_STATUS_TYPE;
}

const OrderDetail = ({
  orderId,
  patientName,
  orderStatus,
}: OrderDetailProps) => {
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const orderDetail = useAppSelector(
    (state) => state.order.orderDetails[orderId]
  );

  const fetchOrderDetail = useCallback(async () => {
    startLoading();
    try {
      const res = await getOrderDetail({
        orderId: orderId,
      });
      if (res.isSuccess) {
        dispatch(
          order.setOrderDetail({
            orderId: orderId,
            data: res.data,
          })
        );
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, orderId, startLoading, stopLoading]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  if (!orderDetail) return;

  return (
    <Container>
      <Section>
        <LeftWrap>
          <OrderOption orderDetail={orderDetail} />
        </LeftWrap>
        <OrderFiles
          orderId={orderId}
          orderDetail={orderDetail}
          orderStatus={orderStatus}
        />
      </Section>
      <Section>
        <LeftWrap>
          <OrderAttachedFiles
            orderDetail={orderDetail}
            orderId={orderId}
            patientName={patientName}
          />
        </LeftWrap>
        <OrderLog orderDetail={orderDetail} />
      </Section>
    </Container>
  );
};

export default OrderDetail;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  border: 1px solid #bdbdbd;
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  padding: 9px 20px 40px 12px;
  box-sizing: border-box;
  margin-bottom: 6px;
`;

const LeftWrap = styled.div`
  width: 495px;
  flex-shrink: 0;
  display: flex;
`;

const Section = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;
  flex: 1;
  gap: 26px;
`;
